import React, { useRef, useState } from 'react';

import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { cn } from '../../../tailwind-base/utils';
import Image from '../Image';
import Link from '../Link';
import useOnFirstView from '../../hooks/useOnFirstView';

import type {
  SanityDeptCta,
  SanityImage as SanityImageType,
} from '../../../models/gatsby-graphql-types';

const isBrowser = typeof window !== 'undefined';

interface Props {
  borderColor?: string;
  cta: SanityDeptCta;
  heading: string;
  image: SanityImageType;
  promotionId?: string;
}

const MarketingCalloutCard = ({
  cta,
  heading,
  image,
  promotionId,
}: Readonly<Props>) => {
  const cardRef = useRef(null);
  const { tt } = useUiComponentsInternationalContext();

  const [isHovering, setIsHovering] = useState(false);

  const isDisabled = cta?.link?.linkType === 'disable';

  const handleDataLayer = event => {
    if (isBrowser) {
      window?.dataLayer?.push({ ecommerce: null });
      window?.dataLayer?.push({
        event,
        ecommerce: {
          promotion_id: promotionId,
        },
      });
    }
  };

  useOnFirstView({
    callback: () => handleDataLayer('view_promotion'),
    observerProps: { threshold: 0.5 },
    ready: !!image || !!cta,
    ref: cardRef,
  });

  return (
    <Link
      className={cn(
        'flex relative w-full h-full aspect-[3/4] z-[1] border-2 border-transparent rounded-lg hover:no-underline',
        cta?.text && cta?.link && !isDisabled ? 'hover:border-cta' : ''
      )}
      link={isDisabled ? null : cta?.link}
      onClick={() => handleDataLayer('select_promotion')}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      ref={cardRef}
    >
      <Image
        alt={tt(s => s.marketingCalloutComponent.marketingCalloutImageAlt)}
        className="absolute h-full w-full rounded-lg [&>img]:w-full"
        ratio={null}
        sanityImage={image}
      />
      <div className="flex flex-col items-start justify-end gap-4 w-full h-full p-4 z-[2] rounded-lg bg-gradient-to-b from-transparent to-black/70">
        {heading && <h2 className="text-white">{heading}</h2>}
        {cta?.text && (
          <span
            className={cn(
              'text-xl text-white font-bold border-b-4 border-transparent transition-all',
              isHovering && !isDisabled ? 'border-cta' : ''
            )}
          >
            {cta?.text}
          </span>
        )}
      </div>
    </Link>
  );
};

export default MarketingCalloutCard;
